import Box from '@mui/material/Box';
import { Formik } from 'formik';
import * as yup from 'yup';
import { MFTextField, MFSubmitButton } from '../../lib/formik';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { emailRegex, phoneRegExp } from '../../utils/regex';
import { CountryCodesDropDown, LoginLayout } from '../commonComponents';
import { getCountryCodes, preventSpecialCharacters } from '../../utils/utilityFunctions';
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Stack, Typography } from '@mui/material';
import { triggerForgotPassword } from '../../redux-store/actions/userManagement';
import { ForgotPasswordResponse } from '../../redux-store/types/user';
import { useSnackbar } from 'notistack';
import { ENABLE_EMAIL } from '../../utils/constant';

interface Values {
  mobile: string;
  countryCode: string;
  countryNameAndCode: string;
  email: string;
}

const validationSchema = yup.object().shape({
  mobile: yup.string().test('mobile', (value, context) => {
    const { createError, path, parent } = context;
    const codesList = getCountryCodes()
      .map((codes) => {
        if (codes.label === parent.countryNameAndCode) {
          return codes.countryCode;
        }
      })
      .filter((ele) => ele)
      .toString();
    if (!ENABLE_EMAIL) {
      if (!value) {
        return createError({
          message: 'Mobile number is required',
          path,
        });
      }
      if (parent.countryNameAndCode === 'India: +91' && !phoneRegExp.test(value)) {
        return createError({
          message: 'Invalid Mobile number',
          path,
        });
      }
      if (!isValidPhoneNumber(value, codesList as CountryCode)) {
        return createError({
          message: 'Invalid Mobile number',
          path,
        });
      }
    }
    return true;
  }),
  countryNameAndCode: yup
    .string()
    .nullable()
    .test('countryNameAndCode', (value, context) => {
      const { createError, path, parent } = context;
      const codesList = getCountryCodes()
        .map((codes) => {
          if (codes.label === value) {
            return codes.countryCode;
          }
        })
        .filter((ele) => ele)
        .toString();
      if (!ENABLE_EMAIL) {
        if (!value) {
          return createError({
            message: 'Country Code is required',
            path,
          });
        }
        if (parent.mobile && !isValidPhoneNumber(parent.mobile, codesList as CountryCode)) {
          return createError({
            message: 'Invalid Mobile number',
            path,
          });
        }
      }
      return true;
    }),
  email: yup
    .string()
    .nullable()
    .when({
      is: () => ENABLE_EMAIL,
      then: yup
        .string()
        .nullable()
        .required('Email id is required')
        .matches(emailRegex, 'Invalid email id'),
    }),
});

export default function PasswordReset(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const onSubmit = async (values: Values) => {
    try {
      const { mobile = '', countryNameAndCode = 'India: +91', email = '' } = values;
      const res = (await dispatch(
        triggerForgotPassword({
          ...(ENABLE_EMAIL
            ? { email: email }
            : { phone: mobile.trim(), countryCode: countryNameAndCode?.split(':')[1].trim() }),
        })
      )) as unknown as ForgotPasswordResponse;
      enqueueSnackbar(res.message, {
        variant: 'success',
        autoHideDuration: 3000,
      });
      history.push('/login');
    } catch (e) {
      console.error((e as unknown as Error).message);
    }
  };
  const initialValues: Values = {
    mobile: '',
    countryCode: '+91',
    countryNameAndCode: 'India: +91',
    email: '',
  };
  const [mobileElementWidth, setMobileElementWidth] = useState(null);
  useEffect(() => {
    const mobileElement = document.getElementsByName('mobile');
    setMobileElementWidth(mobileElement[0]?.parentElement?.clientWidth as any);
  }, []);
  return (
    <LoginLayout LoginText="Forgot password">
      <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={validationSchema}>
        {({ handleSubmit, values }) => (
          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{
              '.MuiLoadingButton-root': {
                mt: 5,
              },
              '.MuiInputLabel-root': {
                fontFamily: 'Poppins, sans-serif',
                fontSize: 16,
                fontWeight: 500,
              },
            }}>
            {!ENABLE_EMAIL ? (
              <MFTextField
                label="Mobile Number"
                name="mobile"
                placeholder="Enter Mobile number"
                onKeyDown={(e) => {
                  preventSpecialCharacters(e);
                }}
                startAdornment={
                  <CountryCodesDropDown
                    ElementWidth={mobileElementWidth && mobileElementWidth}
                    name={'countryNameAndCode'}
                    value={values.countryNameAndCode}
                  />
                }
                autoFocus
              />
            ) : (
              <MFTextField
                autoComplete="off"
                label="Email ID"
                name="email"
                placeholder="Enter Email ID"
                required
              />
            )}
            <Box
              sx={{
                textAlign: 'justify',
                fontSize: 14,
                color: '#d32f2f',
              }}>
              Note: We’ll send a password reset link to this email and mobile number if it matches
              an existing account.
            </Box>
            <MFSubmitButton label="Next" />
            <Stack justifyContent={'center'}>
              <Typography
                component={NavLink}
                to={{ pathname: '/login' }}
                sx={{
                  textAlign: 'center',
                  textDecoration: 'none',
                  color: (theme) => theme.palette.primary.main,
                  fontSize: 14,
                  fontWeight: 500,
                  letterSpacing: 1,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}>
                Back
              </Typography>
            </Stack>
          </Box>
        )}
      </Formik>
    </LoginLayout>
  );
}
